import { useTranslation } from "react-i18next";
import { Card, type CardProps, Text } from "react-native-paper";
import type { IconType } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import Button from "../Button";
import { FlatCard } from "../Cards/FlatCard";
import CustomChip from "../CustomChip";
import { InfoChip } from "../InfoChip";
import { getButtonIconAndLabel } from "./helpers";
import type { TaskChip, TasksSectionItem } from "./types";

type Props = TasksSectionItem & {
	chipLabel?: string;
	style?: CardProps["style"];
};

/**
 * The completion-ring `icon` (when set) leads the chip row as an icon-only
 * chip, ahead of the item-count/duration `chips`.
 */
const ExamActionCardChips = ({
	icon,
	chips,
}: {
	icon?: IconType;
	chips?: TaskChip[];
}) => (
	<Box flexDirection="row" flexWrap="wrap" gap={8}>
		{icon ? <InfoChip key="icon-chip" icon={icon} /> : null}
		{chips?.map((chip) => (
			<InfoChip
				key={chip.label}
				icon={chip.icon}
				label={chip.iconOnly ? undefined : chip.label}
			/>
		))}
	</Box>
);

export const ExamActionCard = ({
	status,
	title,
	subtitle,
	body,
	chips,
	chipLabel,
	kind,
	icon,
	loading: isLoading = false,
	style,
	onPress,
}: Props) => {
	const { t } = useTranslation();
	const { isMobile } = useDevice();
	const theme = useAppTheme();

	const buttonInfo = getButtonIconAndLabel(status, kind);

	const hasChips = Boolean(icon) || Boolean(chips?.length);
	const inlineSubtitle = hasChips ? undefined : subtitle;

	return (
		<FlatCard style={style}>
			<Card.Content
				style={{
					flexDirection: isMobile ? "column" : "row",
					alignItems: isMobile ? "flex-start" : "center",
					justifyContent: "space-between",
					gap: 16,
					width: "100%",
					padding: 16,
				}}
			>
				<Box flex={1} gap={8} width="100%" minWidth={0} alignSelf="stretch">
					{hasChips ? (
						<ExamActionCardChips icon={icon} chips={chips} />
					) : chipLabel ? (
						<CustomChip
							style={{
								backgroundColor: theme.colors.elevation.level1,
								borderRadius: 8,
								alignSelf: "flex-start",
							}}
							textStyle={{
								color: theme.colors.onSurface,
							}}
						>
							{chipLabel}
						</CustomChip>
					) : null}
					<Text variant="titleMedium" numberOfLines={1}>
						{title}
					</Text>
					{body ? (
						<Text
							variant="bodySmall"
							style={{ color: theme.colors.onSurfaceVariant }}
						>
							{body}
						</Text>
					) : null}
				</Box>
				<Box
					width={isMobile ? "100%" : "auto"}
					flexDirection="row"
					alignItems="center"
					justifyContent={
						isMobile && inlineSubtitle ? "space-between" : "flex-end"
					}
					gap={16}
					borderTopWidth={isMobile ? 1 : 0}
					borderColor={theme.colors.surfaceVariant}
					paddingTop={isMobile ? 16 : 0}
				>
					{inlineSubtitle ? (
						<Text variant="bodySmall">{inlineSubtitle}</Text>
					) : null}
					<Button
						disabled={isLoading}
						mode="contained"
						icon={buttonInfo?.icon}
						onPress={onPress}
						loading={isLoading}
					>
						{buttonInfo?.label ?? t("sessionConfig.start")}
					</Button>
				</Box>
			</Card.Content>
		</FlatCard>
	);
};
